import { UcoastVideo } from '@/scripts/core/ucoast-video'
import { throttle, TsDOM as q } from '@/scripts/core/TsDOM'
import { type Hls } from '../global'
import { type ArtDirection } from '@/scripts/core/art-direction'
import { percentageSeen } from '@/scripts/theme/animations'
interface FadeOptions {
	duration?: number
	initialOpacity?: number
}

export class MediaManager {
	hlsRequired: boolean
	hlsLibraryLoaded: boolean
	lastKnownScrollPosition: number
	Hls?: Hls
	playCallbacks: (() => void)[]
	hlsLibIsSupported = false
	lastKnownWindowWidth?: number
	videos: UcoastVideo[]
	priorityImages: ArtDirection[]
	loadedPriorityImages: ArtDirection[]
	videoObserver: IntersectionObserver
	private ticking: boolean = false

	constructor() {
		this.hlsLibraryLoaded = false
		this.lastKnownScrollPosition = 0
		this.ticking = false
		this.playCallbacks = []
		this.hlsRequired = this.isHlsRequired()
		this.videos = []
		this.loadedPriorityImages = []
		this.priorityImages = Array.from(
			q.ol<ArtDirection>('art-direction') ?? []
		).filter(
			(artDirection) =>
				artDirection.isPriority &&
				this.loadedPriorityImages.indexOf(artDirection) === -1
		)
		window.addEventListener('scroll', this.onScroll.bind(this), {
			passive: true,
		})
		this.videoObserver = new IntersectionObserver(
			this.onIntersection.bind(this),
			{
				rootMargin: '0px 0px -50px 0px',
			}
		)
	}

	private onIntersection(
		elements: IntersectionObserverEntry[],
		_: IntersectionObserver
	) {
		elements.forEach((element, _) => {
			if (!isVideoComponent(element.target)) return
			console.log('check')
			if (element.isIntersecting) {
				if (element.target.isTouchingViewport()) {
					void element.target.play()
				} else {
					element.target.pause()
				}
			}
		})
	}

	private onScroll(): void {
		if (!this.ticking) {
			window.requestAnimationFrame(() => {
				this.scrollHandler()
				this.ticking = false
			})
			this.ticking = true
		}
	}

	// public methods
	async initialLoad() {
		document.addEventListener('DOMContentLoaded', () => {
			// image fade in
			// video autoplay inits
			this.loadHls()
			this.initEventListeners()
			this.loadEarlyVideos()
		})
	}

	markPriorityImageLoaded(image: ArtDirection) {
		this.loadedPriorityImages.push(image)
		this.priorityImages = this.priorityImages.filter(
			(priorityImage) => priorityImage !== image
		)
		if (this.priorityImages.length === 0) {
			Array.from(q.ol<ArtDirection>('art-direction') ?? [])
				.filter((artDirection) => artDirection.isDeferred)
				.forEach((artDirection) => {
					artDirection.releaseDeferredStatus()
				})
		}
	}

	private initEventListeners() {
		window.addEventListener('resize', () => {
			const currentWidth = window.innerWidth
			if (currentWidth !== this.lastKnownWindowWidth) {
				this.lastKnownWindowWidth = currentWidth
			}
		})
	}
	loadEarlyVideos() {
		const videos = q.ol<UcoastVideo>('ucoast-video[data-retry="true"]')
		if (!videos) return
		videos.forEach((video) => {
			if (this.videos.some((item) => video === item)) return
			video.unMarkForRetry()
			void video.onConnectedCallback()
		})
	}

	scrollHandler() {
		this.videos.forEach((video) => {
			if (video.isTouchingViewport()) {
				void video.play()
			} else {
				video.pause()
			}
		})
	}

	addVideo(video: UcoastVideo) {
		if (!this.videos.some((item) => video === item)) {
			this.videoObserver.observe(video)
			this.videos.push(video)
		}
	}
	removeVideo(video: UcoastVideo) {
		this.videoObserver.unobserve(video)
		this.videos = this.videos.filter((item) => video !== item)
	}

	// hls

	private isHlsRequired() {
		const video = document.createElement('video')
		return video.canPlayType('application/vnd.apple.mpegurl') === ''
	}

	private async loadHls() {
		if (!this.hlsRequired) return

		try {
			// ignoring because types are defined through global
			// @ts-ignore
			const hlsLib: Hls = await import('hls.js/dist/hls.light.min.js')
			this.hlsLibIsSupported = hlsLib.isSupported()
			this.Hls = hlsLib
			this.hlsLibraryLoaded = true
			q.ol<UcoastVideo>('ucoast-video')?.forEach((video) => {
				void video.onLibraryLoad()
			})
		} catch (error) {
			console.error('Failed to load the HLS library', error)
		}
	}

	async playAllInContainer(container: HTMLElement) {
		const videosInContainer = q.ol<UcoastVideo>('ucoast-video', container)
		if (!videosInContainer) return
		videosInContainer.forEach((video) => {
			void video.playEventOn()
		})
	}

	async pauseAllInContainer(container: HTMLElement) {
		const videosInContainer = q.ol<UcoastVideo>('ucoast-video', container)
		if (!videosInContainer) return
		videosInContainer.forEach((video) => {
			void video.playEventOff()
		})
	}
}

export function isVideoComponent(
	obj: HTMLElement | Element
): obj is UcoastVideo {
	if (!obj) return false
	if (obj.localName !== 'ucoast-video') return false
	return true
}
